import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox, Form, Skeleton } from 'antd'
import { clsx } from 'clsx'

import A11yButton from '@/components/A11yButton'
import B2becSearchSuggestion from '@/components/B2becSearchSuggestion'
import { CustomFormItem } from '@/components/B2bUserForm'
import { DeleteBorderlessIcon as DeleteIcon } from '@/components/Icons'
import { useDebounce } from '@/hooks'

import styles from '../EditWishlistModal.module.scss'
import { getUserSuggestion } from '../helper'
import SharingWishlistItem from '../SharingWishlistItem'

// Check if current collaborator array contain user object
const checkContainsObject = (collaboratorArray, userObj) => {
  return collaboratorArray.some((item) => item?.userId === userObj?.userId)
}

function SharingWishlistForm({ sharedWithCompany, form }) {
  const { t } = useTranslation()

  const [searchText, setSearchText] = useState('')
  const searchQuery = useDebounce(searchText, 300)
  const [status, setStatus] = useState('idle')
  const [options, setOptions] = useState([])
  const [forceResetAutocomplete, setForceResetAutocomplete] = useState()

  const [shouldDisableCollaboratorList, setShouldDisableCollaboratorList] = useState(
    sharedWithCompany,
  )

  const handleSearch = async (text) => {
    setStatus('pending')
    setSearchText(text)
    setOptions([])
  }

  const renderNotFoundContent = (searchTerm, searchStatus) => {
    if (!searchTerm) {
      return undefined
    }

    if (searchStatus === 'pending') {
      return (
        <div style={{ width: '100%' }}>
          {`${t('searchFor')} "${searchTerm}"`}
          <Skeleton />
        </div>
      )
    }

    return <div>{`${t('searchNoResults')} "${searchTerm}"`}</div>
  }

  const addItemToCollaboratorList = useCallback(
    (user) => {
      const currentCollaboratorList = form.getFieldValue('collaborators')
      if (!checkContainsObject(currentCollaboratorList, user)) {
        form.setFieldsValue({
          collaborators: [...currentCollaboratorList, user],
        })
      }
    },
    [form],
  )

  const renderSearchItem = useCallback(
    (user) => {
      return (
        <A11yButton
          elementType="div"
          onClick={() => {
            addItemToCollaboratorList(user)
          }}
        >
          {user?.email}
        </A11yButton>
      )
    },
    [addItemToCollaboratorList],
  )

  useEffect(() => {
    setForceResetAutocomplete(new Date().toTimeString())
    setShouldDisableCollaboratorList(sharedWithCompany)
  }, [sharedWithCompany])

  useEffect(() => {
    async function fetchData() {
      if (!searchQuery) {
        setOptions([])
        setStatus('idle')
        return
      }

      const currentCollaboratorList = form.getFieldValue('collaborators')
      const response = await getUserSuggestion(searchQuery)
      const filteredResponse = response.filter(
        (item) => item?.userId && !checkContainsObject(currentCollaboratorList, item),
      )
      const formattedResponse = filteredResponse.map((item) => ({
        label: renderSearchItem(item),
        value: item?.email,
        key: item?.userId,
      }))

      setOptions(formattedResponse)
      setStatus('idle')
    }
    fetchData()
  }, [form, renderSearchItem, searchQuery])

  return (
    <>
      <CustomFormItem
        className="mt-2"
        name="sharedWithCompany"
        displayLabel={false}
        valuePropName="checked"
        onChange={(e) => {
          setShouldDisableCollaboratorList(e.target.checked)
        }}
      >
        <Checkbox>
          {t('wishlist.modal.editWishlist.everyOneCanAccessWishlist')}
        </Checkbox>
      </CustomFormItem>
      <div style={{ position: 'relative' }}>
        <div
          className={clsx(
            shouldDisableCollaboratorList && styles.disabledLayer,
          )}
        />
        <div className={styles.label}>
          {t('wishlist.modal.editWishlist.inviteCollaborators')}
        </div>
        <B2becSearchSuggestion
          key={forceResetAutocomplete}
          options={options}
          onSearch={handleSearch}
          notFoundContent={renderNotFoundContent(searchText, status)}
          shouldShowRightButton={false}
          shouldDisableInput={shouldDisableCollaboratorList}
        />

        <div className={clsx(styles.list)}>
          {!shouldDisableCollaboratorList && (
            <Form.List
              name="collaborators"
              disabled={shouldDisableCollaboratorList}
            >
              {(fields, { remove }) => {
                return (
                  <>
                    {fields.map((field) => {
                      return (
                        <div
                          key={`collaborators${field.name}`}
                          className={styles.wishlistItem}
                        >
                          <Form.Item
                            name={field.name}
                            {...field}
                            className={styles.wishlistItem_input}
                          >
                            <SharingWishlistItem
                              shouldDisableInput={shouldDisableCollaboratorList}
                            />
                          </Form.Item>
                          <DeleteIcon
                            className={styles.wishlistItem_deleteButton}
                            onClick={() => {
                              if (!shouldDisableCollaboratorList) {
                                remove(field.name)
                              }
                            }}
                          />
                        </div>
                      )
                    })}
                  </>
                )
              }}
            </Form.List>
          )}
        </div>
      </div>
    </>
  )
}

export { SharingWishlistForm }
